import { Star, StarBorder, MoreHoriz } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router';

import { useDeleteFolderMutation } from 'api/mutations/useDeleteFolderMutation';
import { useToggleFolderFavouriteMutation } from 'api/mutations/useToggleFolderFavouriteMutation';
import { useFolderUsageQuery } from 'api/queries/useFolderUsageQuery';
import { FolderItem, FolderUsageFileEntry } from 'api/types';
import { NewConfirmDialog } from 'components';
import { CardsW } from 'features/documentCategories/components/DocumentCategories/styled';
import {
  CardW,
  IconBox,
  CardLeft,
  CardContent,
  TitleText,
  SubText,
  CardActions,
  ListTitle,
  ListMeta,
  FolderCardRightW,
} from 'features/documentCategories/components/DocumentCategoryItem/styled';
import { FolderNavState } from 'features/documentList/container/DocumentListPage/types';
import { Links } from 'settings/paths';
import { theme } from 'styles';
import { formatCreatedAt } from 'utils/dateHelpers';

import { FolderEditCategoryModal } from '../FolderEditCategoryModal';

type Props = {
  item: FolderItem;
  viewMode: 'grid' | 'list';
  tag: string;
  currentFolderId?: string;
};

export const FolderCard = ({ item, viewMode, tag, currentFolderId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const menuJustClosed = useRef(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const createdBy = `${item.created_by.first_name} ${item.created_by.last_name}`;
  const createdAt = formatCreatedAt(item.created_at);
  const icon = <FolderOutlinedIcon sx={{ color: '#f59e0b', width: 20, height: 20 }} />;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    menuJustClosed.current = true;
    setAnchorEl(null);
    setTimeout(() => {
      menuJustClosed.current = false;
    }, 0);
  };

  const handleMenuItemClick = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMenuClose();
    action();
  };

  const { deleteFolderMutation } = useDeleteFolderMutation();
  const { toggleFavourite } = useToggleFolderFavouriteMutation();

  type DeleteDialogState = 'idle' | 'checking' | 'linked' | 'safe';
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>('idle');
  const [linkedFiles, setLinkedFiles] = useState<FolderUsageFileEntry[]>([]);

  const { refetch: checkUsage } = useFolderUsageQuery({ folderId: item.id, enabled: false });

  const handleDeleteTarget = async () => {
    setDeleteDialogState('idle');
    await deleteFolderMutation(item.id);
  };

  const handleDeleteClick = async () => {
    setDeleteDialogState('checking');
    try {
      const result = await checkUsage();
      if (result.data?.is_linked) {
        setLinkedFiles(result.data.files);
        setDeleteDialogState('linked');
      } else {
        setDeleteDialogState('safe');
      }
    } catch {
      setDeleteDialogState('safe');
    }
  };

  const handleFolderClick = () => {
    if (menuJustClosed.current) return;
    const state = location.state as FolderNavState | null;
    const currentTrail = state?.trail ?? [];
    const currentFolderName = state?.folderName;

    const newTrail = currentFolderId
      ? [...currentTrail, { id: currentFolderId, name: currentFolderName ?? currentFolderId }]
      : currentTrail;

    navigate(generatePath(Links.resources.documents.folderView, { tag, folderId: item.id }), {
      state: {
        trail: newTrail,
        folderName: item.name,
      } as FolderNavState,
    });
  };

  const onClickFavourite = () => {
    toggleFavourite({ id: item.id, isFavourite: !!item.is_favourite });
  };

  const menuItems = (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleMenuClose}
      PaperProps={{
        sx: {
          width: 250,
          padding: '8px',
          borderRadius: '8px',
          border: '1px solid #ddd',
        },
      }}
      MenuListProps={{ disablePadding: true }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem
        onClick={handleMenuItemClick(() => setIsEditModalOpen(true))}
        sx={{ height: '44px', '&:hover': { backgroundColor: theme.palette.info.light } }}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('resources.documentCategories.folders.menu.edit')}</ListItemText>
      </MenuItem>

      <MenuItem
        onClick={handleMenuItemClick(handleDeleteClick)}
        sx={{ height: '44px', '&:hover': { backgroundColor: theme.palette.info.light } }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{t('resources.documentCategories.folders.menu.delete')}</ListItemText>
      </MenuItem>
    </Menu>
  );

  const modals = (
    <div
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Dialog
        open={deleteDialogState === 'checking' || deleteDialogState === 'linked'}
        onClose={() => setDeleteDialogState('idle')}
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px', minWidth: 360 } }}
      >
        {deleteDialogState === 'checking' && (
          <DialogContent
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}
          >
            <CircularProgress />
          </DialogContent>
        )}

        {deleteDialogState === 'linked' && (
          <>
            <DialogTitle sx={{ fontWeight: 700 }}>
              {t('document.list.cannotDeleteFolder', { name: item.name })}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('document.list.folderAttachedToPosts', { count: linkedFiles.length })}
              </Typography>
              <List dense disablePadding>
                {linkedFiles.slice(0, 3).map((file) => (
                  <ListItem
                    key={file.file_id}
                    disableGutters
                    sx={{ py: 0.5, flexDirection: 'column', alignItems: 'flex-start' }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {file.display_name}
                    </Typography>
                    {file.linked_posts.slice(0, 2).map((post) => (
                      <Typography
                        key={post.id}
                        variant="body2"
                        color="primary"
                        sx={{ pl: 1, cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                        onClick={() => {
                          setDeleteDialogState('idle');
                          navigate(generatePath(Links.we.posts.index, { postId: post.id }));
                        }}
                      >
                        &bull; {post.title}
                      </Typography>
                    ))}
                    {file.linked_posts.length > 2 && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontStyle="italic"
                        sx={{ pl: 1 }}
                      >
                        +{file.linked_posts.length - 2} more
                      </Typography>
                    )}
                  </ListItem>
                ))}
                {linkedFiles.length > 3 && (
                  <ListItem disableGutters sx={{ py: 0 }}>
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      +{linkedFiles.length - 3} more file{linkedFiles.length - 3 !== 1 ? 's' : ''}
                    </Typography>
                  </ListItem>
                )}
              </List>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={() => setDeleteDialogState('idle')}>
                {t('resources.documentCategories.folders.removeModal.buttons.cancel')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <NewConfirmDialog
        title={t('resources.documentCategories.folders.removeModal.title', {
          folderTitle: item.name,
        })}
        subTitle={t('resources.documentCategories.folders.removeModal.subtitle')}
        open={deleteDialogState === 'safe'}
        onConfirm={handleDeleteTarget}
        onClose={() => setDeleteDialogState('idle')}
      />
      <FolderEditCategoryModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        itemName={item.name}
        itemDescription="description"
        folderId={item.id}
        update
      />
    </div>
  );

  if (viewMode === 'list') {
    return (
      <CardW
        isManageable
        viewMode="list"
        onClick={(e) => {
          handleFolderClick();
          e.stopPropagation();
        }}
      >
        <IconBox>{icon}</IconBox>
        <ListTitle>{item.name}</ListTitle>
        <FolderCardRightW>
          <ListMeta>{createdBy}</ListMeta>
          <ListMeta>{createdAt}</ListMeta>
        </FolderCardRightW>
        <CardActions>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClickFavourite();
            }}
          >
            {item.is_favourite ? (
              <Star fontSize="small" sx={{ color: '#f59e0b' }} />
            ) : (
              <StarBorder fontSize="small" />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClick(e);
            }}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
          {menuItems}
        </CardActions>
        {modals}
      </CardW>
    );
  }

  return (
    <CardW
      isManageable
      viewMode="grid"
      onClick={(e) => {
        handleFolderClick();
        e.stopPropagation();
      }}
    >
      <CardLeft>
        <IconBox>{icon}</IconBox>
        <CardContent>
          <TitleText>{item.name}</TitleText>
          <SubText>{createdBy}</SubText>
          <SubText>{createdAt}</SubText>
        </CardContent>
      </CardLeft>
      <CardActions>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClickFavourite();
          }}
        >
          {item.is_favourite ? (
            <Star fontSize="small" sx={{ color: '#f59e0b' }} />
          ) : (
            <StarBorder fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleClick(e);
          }}
        >
          <MoreHoriz fontSize="small" />
        </IconButton>
        {menuItems}
      </CardActions>
      {modals}
    </CardW>
  );
};

type ListProps = {
  folders: FolderItem[];
  viewMode: 'grid' | 'list';
  tag: string;
  currentFolderId?: string;
};

export const FolderCardList = ({ folders, viewMode, tag, currentFolderId }: ListProps) => {
  const { t } = useTranslation();
  if (!folders.length) return null;

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>
        {t('resources.documentCategories.folders.title')}
      </div>
      <CardsW viewMode={viewMode}>
        {[...folders]
          .sort((a, b) => (b.is_favourite ? 1 : 0) - (a.is_favourite ? 1 : 0))
          .map((folder) => (
            <FolderCard
              key={folder.id}
              item={folder}
              viewMode={viewMode}
              tag={tag}
              currentFolderId={currentFolderId}
            />
          ))}
      </CardsW>
    </div>
  );
};
